import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
} from '@kollektive/design-system';
import { AlertTriangle } from 'lucide-react';
import type { Connection } from '@/types';
import styles from './DeleteConnectionDialog.module.css';

interface DeleteConnectionDialogProps {
  connection: Connection;
  connectionCount?: number;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

export function DeleteConnectionDialog({
  connection,
  connectionCount = 1,
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}: DeleteConnectionDialogProps) {
  const isBulk = connectionCount > 1;
  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose} size="sm">
      <ModalBody>
        <div className={styles.content}>
          {/* Warning icon */}
          <div className={styles.iconWrapper}>
            <AlertTriangle size={24} className={styles.errorIcon} />
          </div>

          <h2 className={styles.title}>
            {isBulk ? `Delete ${connectionCount} Connections` : 'Delete Connection'}
          </h2>

          <p className={styles.description}>
            {isBulk ? (
              <>
                Are you sure you want to delete <strong>{connectionCount} connections</strong>?
                This action cannot be undone and any AI tools using these connections
                will immediately lose database access.
              </>
            ) : (
              <>
                Are you sure you want to delete the connection{' '}
                <strong>{connection.name}</strong>? This action cannot be undone
                and any AI tools using this connection will immediately lose database access.
              </>
            )}
          </p>

          {/* Connection info - only show for single connection */}
          {!isBulk && (
            <Card>
              <CardBody>
                <div className={styles.connectionInfo}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Host</span>
                    <span className={styles.infoValue}>{connection.host}:{connection.port}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Database</span>
                    <span className={styles.infoValue}>{connection.database_name}</span>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={onConfirm}
          disabled={isLoading}
        >
          {isLoading
            ? 'Deleting...'
            : isBulk
              ? `Delete ${connectionCount} Connections`
              : 'Delete Connection'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
